import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface DiscoverImage {
  id: string;
  url: string;
  width: number;
  height: number;
  aspectRatio: number;
  filename: string;
}

interface UseDiscoverImagesReturn {
  images: DiscoverImage[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  checkBucketAccess: () => Promise<any>;
  stats: {
    totalCount: number;
    displayedCount: number;
    remainingCount: number;
  };
}

const BATCH_SIZE = 20;

// Utility function to shuffle an array using the Fisher-Yates algorithm
function shuffleArray<T>(array: T[]): T[] {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
}

// Function to get the dimensions of an image given its URL
const getImageDimensions = async (url: string): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height
      });
    };
    img.onerror = () => {
      // Fallback to default dimensions if image loading fails
      resolve({
        width: 300,  // Default fallback width
        height: 300  // Default fallback height
      });
    };
    img.src = url;
  });
};

// Function to safely encode a file name for URLs
const encodeFileName = (filename: string): string => {
  // Check if the filename already contains encoded characters like %20
  const hasEncodedChars = /%[0-9A-F]{2}/.test(filename);
  
  if (hasEncodedChars) {
    // If the filename is already encoded, we need to decode it first and then re-encode it properly
    // This fixes double-encoding issues where %20 becomes %2520
    try {
      const decodedFilename = decodeURIComponent(filename);
      // Now encode it correctly
      return encodeURIComponent(decodedFilename);
    } catch (e) {
      // If decoding fails (invalid encoding), fall back to direct encoding of path segments
      // Split by path segments and encode each one
      const segments = filename.split('/');
      const encodedSegments = segments.map(segment => encodeURIComponent(segment));
      return encodedSegments.join('/');
    }
  } else {
    // Normal encoding for filenames without encoded characters
    const segments = filename.split('/');
    const encodedSegments = segments.map(segment => encodeURIComponent(segment));
    return encodedSegments.join('/');
  }
};

// Function to fix a Supabase URL that might contain special characters
const fixSupabaseUrl = (url: string, filename: string): string => {
  // Check if the filename contains characters that need special handling
  if (filename.includes('%') || filename.includes('?') || 
      filename.includes('&') || filename.includes('#') || 
      filename.includes('+') || filename.includes(' ')) {
    
    // Extract the base URL and the path
    const urlParts = url.split('/object/public/');
    if (urlParts.length === 2) {
      const baseUrl = urlParts[0];
      const pathParts = urlParts[1].split('/');
      const bucket = pathParts[0];
      const path = pathParts.slice(1).join('/');
      
      // Rebuild the URL with proper encoding
      return `${baseUrl}/object/public/${bucket}/${encodeFileName(path)}`;
    }
  }
  
  return url;
};

export function useDiscoverImages(): UseDiscoverImagesReturn {
  const [images, setImages] = useState<DiscoverImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [displayedCount, setDisplayedCount] = useState(0);
  const [allImages, setAllImages] = useState<DiscoverImage[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  
  // Stats for debug purposes
  const stats = {
    totalCount: allImages.length,
    displayedCount: images.length,
    remainingCount: allImages.length - images.length
  };

  // Function to directly check bucket access for debugging
  const checkBucketAccess = async () => {
    try {
      // 1. Check auth status
      const { data: sessionData } = await supabase.auth.getSession();
      
      // 2. Try listing all buckets
      const { data: bucketsData, error: bucketsError } = await supabase.storage.listBuckets();
      
      // 3. Try direct access to discover bucket
      const { data: filesData, error: filesError } = await supabase.storage
        .from('discover')
        .list('');
      
      // 4. Try getting URL for first file if any
      let testUrl = null;
      let fetchResult = null;
      
      if (filesData && filesData.length > 0) {
        // Look for a file with special characters for testing
        const specialCharFile = filesData.find(f => 
          f.name.includes('%') || f.name.includes('?') || 
          f.name.includes(' ') || f.name.includes('&')
        );
        
        const fileName = specialCharFile ? specialCharFile.name : filesData[0].name;
        
        const { data: urlData } = supabase.storage
          .from('discover')
          .getPublicUrl(fileName);
        
        // Fix the URL if it contains special characters
        testUrl = urlData.publicUrl;
        
        // Apply our URL fixing function
        const fixedUrl = fixSupabaseUrl(testUrl, fileName);
        if (fixedUrl !== testUrl) {
          testUrl = fixedUrl;
        }
        
        // 5. Try fetching the image directly
        try {
          const testFetch = await fetch(testUrl, { method: 'HEAD' });
          fetchResult = {
            status: testFetch.status,
            ok: testFetch.ok
          };
        } catch (fetchErr) {
          fetchResult = {
            error: fetchErr instanceof Error ? fetchErr.message : 'Unknown fetch error'
          };
        }
      }
      
      // Return comprehensive debug information
      return {
        authStatus: !!sessionData.session,
        buckets: {
          success: !bucketsError,
          count: bucketsData?.length || 0,
          names: bucketsData?.map(b => b.name) || [],
          error: bucketsError?.message
        },
        discoverBucket: {
          success: !filesError,
          fileCount: filesData?.length || 0,
          sampleFiles: filesData?.slice(0, 3).map(f => f.name) || [],
          error: filesError?.message
        },
        fileAccess: {
          testUrl,
          fetchResult
        }
      };
    } catch (err) {
      return {
        error: err instanceof Error ? err.message : 'Unknown error during direct test'
      };
    }
  };

  // Function to fetch all images initially from the discover bucket
  const fetchAllImages = useCallback(async () => {
    if (loading) return;
    
    setLoading(true);
    setError(null);
    setLoadError(null);

    try {
      const { data, error: listError } = await supabase.storage
        .from('discover')
        .list('', {
          sortBy: { column: 'name', order: 'asc' },
          limit: 1000, // Explicitly set a high limit
        });

      if (listError) {
        throw listError;
      }

      if (!data || data.length === 0) {
        setHasMore(false);
        setInitialLoadComplete(true);
        return;
      }
      
      // Filter out any folders or non-image files
      const imageFiles = data
        .filter(item => !item.id.endsWith('/')) // Filter out folders
        .filter(file => {
          const ext = file.name.split('.').pop()?.toLowerCase();
          return ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'gif' || ext === 'webp';
        });
      
      // Process the files and get URLs
      const fetchedImages = await Promise.all(
        imageFiles.map(async (file) => {
          // Get the public URL from Supabase
          const { data: urlData } = supabase.storage
            .from('discover')
            .getPublicUrl(file.name);

          // Fix the URL if it contains special characters
          let publicUrl = fixSupabaseUrl(urlData.publicUrl, file.name);

          // Get actual image dimensions
          const dimensions = await getImageDimensions(publicUrl);
          const aspectRatio = dimensions.width / dimensions.height;

          return {
            id: file.id,
            url: publicUrl,
            width: dimensions.width,
            height: dimensions.height,
            aspectRatio,
            filename: file.name
          };
        })
      );

      // Shuffle all images for randomness
      const shuffled = shuffleArray(fetchedImages);
      setAllImages(shuffled);
      
      // Display the first batch
      const initialBatch = shuffled.slice(0, BATCH_SIZE);
      
      setImages(initialBatch);
      setDisplayedCount(initialBatch.length);
      setHasMore(shuffled.length > initialBatch.length);
      setInitialLoadComplete(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch discover images');
      setLoadError(err instanceof Error ? err.message : 'Failed to fetch discover images');
      setInitialLoadComplete(true);
    } finally {
      setLoading(false);
    }
  }, [loading]);

  // Function to load more images (from the already fetched and shuffled array)
  const loadMore = useCallback(async () => {
    if (loading || !hasMore) {
      return;
    }
    
    setLoading(true);
    
    // Short timeout to ensure UI updates
    setTimeout(() => {
      try {
        const nextBatchSize = Math.min(BATCH_SIZE, allImages.length - displayedCount);
        
        if (nextBatchSize <= 0) {
          setHasMore(false);
          setLoading(false);
          return;
        }
        
        const nextBatch = allImages.slice(displayedCount, displayedCount + nextBatchSize);
        
        setImages(prevImages => [...prevImages, ...nextBatch]);
        
        const newDisplayedCount = displayedCount + nextBatch.length;
        setDisplayedCount(newDisplayedCount);
        
        // Check if we have more images to load after this batch
        const remainingCount = allImages.length - newDisplayedCount;
        setHasMore(remainingCount > 0);
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'Failed to load more images');
      } finally {
        setLoading(false);
      }
    }, 100);
  }, [loading, hasMore, allImages, displayedCount, images.length]);

  // Initial load
  useEffect(() => {
    fetchAllImages();
  }, []);

  return {
    images,
    loading,
    error: error || loadError,
    hasMore,
    loadMore,
    checkBucketAccess,
    stats
  };
} 